/**
 * Mana symbols, drawn as the official symbols rather than spelled out.
 *
 * Scryfall writes costs and rules text with the symbol in braces — `{1}{W}`,
 * `{G/W}`, `{T}` — which is precise and unreadable. This turns each one into
 * the symbol itself.
 *
 * The images come from Scryfall's symbol CDN, whose URL is the symbol with the
 * braces and any slash removed: `{W/U}` is `WU.svg`, `{2/G}` is `2G.svg`,
 * `{10}` is `10.svg`. I checked that rule against all 84 symbols Scryfall
 * publishes and it holds for 82 of them; the two that don't are named below.
 * Nothing is vendored because this page already shows Scryfall's card images,
 * so the same CDN being reachable is not a new condition.
 */

import type { ReactNode } from 'react';

const BASE = 'https://svgs.scryfall.io/card-symbols/';

/** The two symbols whose file is not just the symbol with its braces off. */
const ODD: Record<string, string> = { '½': 'HALF', '∞': 'INFINITY' };

const file = (inner: string) => ODD[inner] ?? inner.replace(/\//g, '').toUpperCase();

/**
 * What a symbol is called out loud, for anyone using a screen reader. Costs
 * read as "one, white" rather than a run of punctuation.
 */
const NAMES: Record<string, string> = {
  W: 'white',
  U: 'blue',
  B: 'black',
  R: 'red',
  G: 'green',
  C: 'colourless',
  S: 'snow',
  X: 'X',
  T: 'tap',
  Q: 'untap',
  E: 'energy',
  P: 'Phyrexian',
};

function label(inner: string): string {
  if (NAMES[inner]) return NAMES[inner];
  if (/^\d+$/.test(inner)) return inner;
  // Hybrids read as their halves: {G/W} is "green or white".
  return inner
    .split('/')
    .map((h) => NAMES[h] ?? h)
    .join(' or ');
}

/** One symbol, at the current text size. */
export function Mana({ symbol }: { symbol: string }) {
  const inner = symbol.replace(/^\{|\}$/g, '');
  return (
    <img className="mana" src={`${BASE}${file(inner)}.svg`} alt={label(inner)} loading="lazy" />
  );
}

/**
 * A string with every `{…}` replaced by its symbol, and everything else left
 * alone. Used for costs, for rules text, and for the guide's cost column.
 */
export function withMana(text: string): ReactNode[] {
  return text.split(/(\{[^}]+\})/g).map((part, i) =>
    /^\{[^}]+\}$/.test(part) ? <Mana key={i} symbol={part} /> : part,
  );
}
